import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

const API_URL = import.meta.env.VITE_API_URL;

// Module-level variable to hold the data loader pause function. This allows the non-React axios component in api-utils
//  to coordinate with the DataLoaderContext.
let dataLoaderPauseFunction = null;

// Flag to track if we're already handling an unauthorized state to prevent multiple concurrent redirects
// This flag is set when the auth tokens are expired and the refresh token is also expired. It signals a state where the
// current session is effectively dead, and the user will have to be directed to sign in again.
let isHandlingUnauthorized = false;

/** While true, transient 401s from token refresh must not surface as user-facing errors.
 * This flag is set when the refresh token is still present and usable, and suppresses transient 401 errors while it is
 * being exchanged for new auth tokens. */
let authRefreshInProgress = false;

/**
 * Register the data loader pause function to be called when unauthorized
 * @param {Function} pauseFn - Function to pause the data loader
 */
export const registerDataLoaderPause = (pauseFn) => {
  dataLoaderPauseFunction = pauseFn;
};

const defaultAxiosOpts = {
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true,
  maxRedirects: 0,
};

const axiosInstance = axios.create(defaultAxiosOpts);

/** Cancels in-flight shared-axios requests when aborted; rotated on each tenant switch. */
let tenantRequestAbortController = new AbortController();

/** While true, 401s from aborted in-flight calls must not trigger auth refresh. */
let tenantSwitchInProgress = false;

/**
 * @param {boolean} inProgress
 */
export function setTenantSwitchInProgress(inProgress) {
  tenantSwitchInProgress = inProgress;
}

/**
 * @param {import('axios').InternalAxiosRequestConfig} config
 */
function attachTenantAbortSignal(config) {
  const url = config.url || "";
  if (url.includes("/DetechtionAccount/")) {
    return config;
  }
  const tenantSig = tenantRequestAbortController.signal;
  const existing = config.signal;
  if (existing) {
    if (typeof AbortSignal.any === "function") {
      config.signal = AbortSignal.any([existing, tenantSig]);
    }
  } else {
    config.signal = tenantSig;
  }
  return config;
}

axiosInstance.interceptors.request.use(attachTenantAbortSignal);

/** Cancel in-flight shared-axios requests after a tenant switch. */
export function abortInFlightRequests() {
  tenantRequestAbortController.abort();
  tenantRequestAbortController = new AbortController();
}

/** @param {unknown} error */
export function isRequestAborted(error) {
  return axios.isCancel(error) || error?.code === "ERR_CANCELED";
}

/** @param {unknown} error */
function getErrorMessage(error) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return error?.message;
}

/**
 * @param {string|undefined|null} value
 * @returns {boolean}
 */
function isAxios401Message(value) {
  return typeof value === "string" && /status code 401/i.test(value);
}
/**
 * @param {string} [location]
 * @returns {boolean}
 */
function isAccessDeniedRedirect(location) {
  return (
    typeof location === "string" && /AccessFailed|AccessDenied/i.test(location)
  );
}

/** @param {unknown} error */
function isBenignAuthRefreshFailure(error) {
  if (tenantSwitchInProgress || isRequestAborted(error)) return true;
  const status = error?.response?.status;
  if (status === 404) return true;
  const url = error?.config?.url || error?.response?.config?.url || "";
  if (isAccessDeniedRedirect(url)) return true;
  return false;
}

/** @param {unknown} error */
function isTokenRefresh401(error) {
  if (error?.response?.status === 401) {
    const location = error?.response?.headers?.location;
    if (typeof location === "string") {
      if (isAccessDeniedRedirect(location)) return false;
      if (
        /ReturnUrl=/i.test(location)
        || /\/DetechtionAccount\/Login/i.test(location)
      ) {
        return true;
      }
    }
    return false;
  }

  const message = getErrorMessage(error);
  if (isAxios401Message(message)) return authRefreshInProgress;
  return false;
}

/**
 * True when an API failure is session/auth plumbing (token refresh, expiry), not a
 * user-actionable error that should appear in the error banner.
 * @param {unknown} error
 * @returns {boolean}
 */
export function shouldReportApiError(error) {
  if (error == null) return true;
  if (isRequestAborted(error)) return false;
  if (isBenignAuthRefreshFailure(error)) return false;
  if (isTokenRefresh401(error)) return false;
  if (error instanceof Error && /^Auth refresh /i.test(error.message || "")) {
    return false;
  }
  return true;
}

/**
 * @param {unknown[]} args - console.error arguments
 * @returns {unknown|undefined}
 */
export function findApiErrorInConsoleArgs(args) {
  for (const arg of args) {
    if (arg instanceof Error) return arg;
    if (arg?.response?.status != null) return arg;
  }
  return undefined;
}

/**
 * Whether a console.error call should be promoted to the global error banner.
 * @param {string} message
 * @param {string|null|undefined} details
 * @param {unknown[]} args
 * @returns {boolean}
 */
export function shouldReportConsoleError(message, details, args) {
  const apiError = findApiErrorInConsoleArgs(args);
  if (apiError != null && !shouldReportApiError(apiError)) return false;

  if (/Error refreshing tokens/i.test(message)) return false;
  // RCA Explain-this-shutdown already shows an inline failure note.
  if (/runLLMStream failed/i.test(message)) return false;

  if (authRefreshInProgress && apiError == null) {
    const combined = `${message} ${details ?? ""}`;
    if (isAxios401Message(combined) || /^HTTP 401:/i.test(details ?? "")) {
      return false;
    }
  }

  return true;
}

/**
 * Whether a direct `addError` call should be shown in the global banner.
 * @param {string} message
 * @param {string|null|undefined} details
 * @param {unknown} [error]
 * @returns {boolean}
 */
export function shouldReportBannerError(message, details, error) {
  if (error != null) return shouldReportApiError(error);
  if (/Error refreshing tokens/i.test(message)) return false;
  if (authRefreshInProgress) {
    const combined = `${message} ${details ?? ""}`;
    if (isAxios401Message(combined) || /^HTTP 401:/i.test(details ?? "")) {
      return false;
    }
  }
  return true;
}

function parseAuthRefreshBody(data) {
  const trimmed = typeof data === "string" ? data.trim() : "";
  if (!trimmed || trimmed.startsWith("<")) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

// Auth refresh function for use with axios-auth-refresh library
// In effect, this will be called after a 401 is received.
const refreshAuthTokens = async (rsp) => {
  if (tenantSwitchInProgress) {
    throw new Error("Auth refresh skipped during tenant switch");
  }

  const response = rsp.response;
  const location = response?.headers?.location;
  // Only refresh genuine 401s that carry a redirect location. For any other
  // failure (e.g. a 404), preserve the original error so callers can handle it
  // instead of masking it as "Auth refresh missing redirect location".
  if (response?.status !== 401 || typeof location !== "string") {
    throw rsp;
  }

  if (isAccessDeniedRedirect(location)) {
    throw new Error("Auth refresh skipped for access denied redirect");
  }

  const returnUrlIndex = location.indexOf("?ReturnUrl=");
  if (returnUrlIndex < 0) {
    throw new Error("Auth refresh redirect missing ReturnUrl");
  }

  const currentUrl = window.location.href;
  const cutoff = location.substring(0, returnUrlIndex + 11);
  const redirect_url = cutoff + encodeURIComponent(currentUrl);

  authRefreshInProgress = true;
  try {
    const response = await axiosInstance.get(redirect_url, {
      headers: {
        EnbaseXT: "true",
      },
      transformResponse: [
        (data) => {
          if (!data || data === "") {
            return;
          }

          const authResponse = parseAuthRefreshBody(data);
          if (!authResponse) {
            return;
          }

          if (authResponse.status == 1) {
            //Auth successful, nop
            return authResponse;
          } else if (authResponse.status == 2) {
            //Credentials are missing, redirect to launchpad
            window.location.href = authResponse.location;
          } else if (authResponse.status == 3) {
            //Missing EULA (Use Dashboard page to accept for now)
            window.location.href = `${API_URL}/preAuth.html?type=eula&ReturnUrl=${encodeURIComponent(currentUrl)}`;
          } else {
            // All other statuses (including 5 - Unauthorized)

            // Only handle if we're not already processing an unauthorized state
            if (!isHandlingUnauthorized) {
              isHandlingUnauthorized = true;

              // Pause the data loader to prevent repetitive unauthorized API calls
              // Use setTimeout to defer the call and avoid interfering with axios response processing.
              if (dataLoaderPauseFunction) {
                setTimeout(() => {
                  dataLoaderPauseFunction();
                }, 0);
              }

              // Then redirect only if not already on the unauthorized page to prevent loops
              if (window.location.pathname !== "/unauthorized") {
                window.location.href = "/unauthorized";
              }
            }
          }
          return authResponse;
        },
      ],
    });

    const authResponse = response.data;

    if (authResponse && authResponse.status == 1) {
      console.log("Auth tokens refreshed successfully");
    } else {
      // Auth refresh failed - throw error to stop axios-auth-refresh from retrying
      throw new Error("Auth refresh failed");
    }
  } catch (err) {
    if (!isBenignAuthRefreshFailure(err)) {
      console.error("Error refreshing tokens", err);
    }
    // Re-throw to ensure axios-auth-refresh knows the refresh failed
    throw err;
  } finally {
    authRefreshInProgress = false;
  }
};

// Attach auth refresh function to Axios interceptor. This library will retry requests once, and queue any requests
// that failed on a 401 for retry after auth tokens are received.
createAuthRefreshInterceptor(axiosInstance, refreshAuthTokens, {
  shouldRefresh: (error) => {
    // Only attempt token refresh for genuine 401s that carry a redirect
    // location. Other failures (e.g. a 404) must not enter the refresh flow,
    // so callers receive the original error instead of a masked refresh error.
    if (tenantSwitchInProgress || isRequestAborted(error)) return false;
    if (error?.response?.status !== 401) return false;
    const location = error?.response?.headers?.location;
    if (typeof location !== "string") return false;
    if (isAccessDeniedRedirect(location)) return false;
    return true;
  },
  pauseInstanceWhileRefreshing: true,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    if (
      status === 403
      && data
      && typeof data === "object"
      && data.code === "OneViewDisabledForTenant"
    ) {
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
